import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import useGameState from '../gameState';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import { APP_CATALOG, getInstalledApps } from '../installedApps';
import { runCommand } from '../terminal/commands';
import { createFs, PREV_OPERATOR_TRACE } from '../terminal/fs';

/**
 * TERMINAL app (ADR-0025 items 3 and 4, ADR-0020 shell).
 *
 * A prompt, a scrollback and history over the same store actions the GUI uses
 * (see ../terminal/commands.js). Nothing here is terminal-only: every command
 * is reachable from an app. Text entry uses one off-screen DOM <input> so the
 * device keyboard works on mobile; the canvas only draws.
 */

const MAX_LINES = 500;
const MAX_INPUT = 120;
const LINE_H = 16;
const CHAR_W = 8;
const VIEW_ID = 'terminal_window';

const state = {
  lines: [],
  history: [],
  histIdx: -1,
  draft: '',
  busy: false,
  scroll: 0,
  booted: false,
  prevRevealed: false,
  pendingTrace: false,
  lastRender: 0,
};

let inputEl = null;
let watchdog = null;

// ---------------------------------------------------------------- deps wiring

function sessionState() {
  return useCoreSession.getState().state || null;
}

function shortName(value) {
  return String(value || '--').replace(/\s*\(.*\)$/, '').toUpperCase();
}

async function getContent() {
  return useCoreSession.getState().loadContent();
}

async function getAssets() {
  const content = await getContent();
  return (content?.assets || [])
    .filter((a) => a.market === 'public' && a.tradable !== false)
    .map((a) => ({
      id: a.assetId,
      symbol: String(a.assetId || '').toUpperCase(),
      streetName: a.streetName,
      name: shortName(a.streetName || a.name || a.assetId),
    }));
}

const fs = createFs({
  getContent,
  getFragments: async () => {
    await useCoreSession.getState().ensureSession();
    return useCoreSession.getState().loadFragments();
  },
  prevRevealed: () => state.prevRevealed,
});

const deps = {
  catalog: APP_CATALOG,
  isInstalled: (id) => getInstalledApps().includes(id),
  getAssets,
  owned: (assetId) => {
    const slot = (sessionState()?.inventory || []).find((item) => item.assetId === assetId);
    return slot?.quantity ?? 0;
  },
  quote: (assetId, side, qty) => useCoreSession.getState().quoteTrade(assetId, side, qty),
  execute: (assetId, side, qty) => useCoreSession.getState().executeTrade(assetId, side, qty),
  getStatus: async () => {
    try {
      await useCoreSession.getState().refreshState();
    } catch {
      return { online: false };
    }
    const core = useGameState.getState().core || {};
    const content = await getContent().catch(() => null);
    const district = (content?.districts || []).find((d) => d.districtId === core.districtId);
    return {
      online: core.status === 'online',
      cash: core.cash,
      debt: core.debt,
      heat: core.heat,
      day: core.day,
      turnsRemaining: core.turnsRemaining,
      district: district?.name || core.districtId,
    };
  },
  loadLoans: async () => {
    await useCoreSession.getState().ensureSession();
    return useCoreSession.getState().loadLoans();
  },
  navigate: (view) => navigateTo(view),
  errMsg: coreErrorMessage,
  fs,
};

// ------------------------------------------------------------------- scrollback

function push(lines) {
  state.lines.push(...lines);
  if (state.lines.length > MAX_LINES) state.lines.splice(0, state.lines.length - MAX_LINES);
  state.scroll = 0;
}

function boot() {
  state.booted = true;
  push([
    { t: 'DOPEOS TERMINAL 1.0', k: 'out' },
    { t: 'LINE OPEN. TYPE help.', k: 'dim' },
  ]);
}

async function submit(raw) {
  const line = String(raw ?? '').trim();
  if (state.busy || !line) return;
  if (state.history[state.history.length - 1] !== line) state.history.push(line);
  state.histIdx = -1;
  state.draft = '';
  if (inputEl) inputEl.value = '';
  push([{ t: `> ${line}`, k: 'cmd' }]);
  state.busy = true;
  try {
    const result = await runCommand(line, deps);
    if (result.clear) state.lines = [];
    push(result.lines);
  } finally {
    state.busy = false;
  }
}

function historyStep(dir) {
  if (!state.history.length || !inputEl) return;
  if (state.histIdx === -1) {
    if (dir > 0) return;
    state.draft = inputEl.value;
    state.histIdx = state.history.length - 1;
  } else {
    state.histIdx += dir < 0 ? -1 : 1;
    if (state.histIdx >= state.history.length) {
      state.histIdx = -1;
      inputEl.value = state.draft;
      return;
    }
    state.histIdx = Math.max(0, state.histIdx);
  }
  inputEl.value = state.history[state.histIdx];
}

/** Test/inspection surface. Read-only snapshot. */
export function getTerminalState() {
  return {
    lines: state.lines.map((l) => ({ ...l })),
    history: [...state.history],
    busy: state.busy,
    prevRevealed: state.prevRevealed,
    input: inputEl ? inputEl.value : '',
  };
}

export function _resetTerminalState() {
  Object.assign(state, {
    lines: [], history: [], histIdx: -1, draft: '', busy: false, scroll: 0,
    booted: false, prevRevealed: false, pendingTrace: false, lastRender: 0,
  });
}

// ------------------------------------------------------------------- DOM input

function isTerminalActive() {
  return typeof window !== 'undefined' && window.DCW_CURRENT_VIEW === VIEW_ID;
}

function removeInput() {
  if (watchdog) clearInterval(watchdog);
  watchdog = null;
  if (inputEl && inputEl.parentNode) inputEl.parentNode.removeChild(inputEl);
  inputEl = null;
}

function ensureInput() {
  if (typeof document === 'undefined') return;
  state.lastRender = Date.now();
  if (inputEl) return;
  inputEl = document.createElement('input');
  inputEl.type = 'text';
  inputEl.setAttribute('data-testid', 'terminal-input');
  inputEl.setAttribute('aria-label', 'Terminal command');
  inputEl.setAttribute('autocapitalize', 'off');
  inputEl.setAttribute('autocomplete', 'off');
  inputEl.setAttribute('autocorrect', 'off');
  inputEl.setAttribute('spellcheck', 'false');
  inputEl.setAttribute('enterkeyhint', 'send');
  inputEl.maxLength = MAX_INPUT;
  // Off-screen but focusable; 16px font stops iOS from zooming on focus.
  inputEl.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0;'
    + 'border:0;padding:0;margin:0;font-size:16px;pointer-events:none;z-index:2147483000;';
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit(inputEl.value);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      historyStep(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      historyStep(1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      navigateTo('main_menu');
    } else if (e.key === 'Tab') {
      e.preventDefault();
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      state.scroll += 8;
    } else if (e.key === 'PageDown') {
      e.preventDefault();
      state.scroll = Math.max(0, state.scroll - 8);
    }
  });
  document.body.appendChild(inputEl);
  // Desktop: take focus so typing just works. Touch: the prompt tap focuses
  // (a focus() outside a gesture would not raise the keyboard anyway).
  if (!(typeof window !== 'undefined' && 'ontouchstart' in window)) {
    try { inputEl.focus({ preventScroll: true }); } catch { /* ignore */ }
  }
  watchdog = setInterval(() => {
    if (Date.now() - state.lastRender > 500 || !isTerminalActive()) removeInput();
  }, 400);
}

function focusInput() {
  ensureInput();
  try { inputEl.focus({ preventScroll: true }); } catch { /* ignore */ }
}

// ---------------------------------------------------------------- global keys

const KONAMI = ['arrowup', 'arrowup', 'arrowdown', 'arrowdown', 'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a'];
let konamiPos = 0;

function showPrevOperatorTrace() {
  state.prevRevealed = true;
  if (!state.booted) boot();
  state.histIdx = -1;
  if (inputEl) inputEl.value = '';
  push([
    { t: '> cat ~/.prev_operator', k: 'cmd' },
    ...PREV_OPERATOR_TRACE.map((t) => ({ t, k: 'glitch' })),
  ]);
}

function onGlobalKeyDown(e) {
  if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
  const view = window.DCW_CURRENT_VIEW;
  // "DopeOS focused": a running desktop, not boot/login, and not another text field.
  const target = e.target;
  const typingElsewhere = target && target !== inputEl
    && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
  if (!view || typingElsewhere || ['boot', 'unbranded', 'loading', 'login'].includes(view)) {
    konamiPos = 0;
    return;
  }
  const key = String(e.key).toLowerCase();
  if (key === KONAMI[konamiPos]) {
    konamiPos += 1;
    if (konamiPos === KONAMI.length) {
      konamiPos = 0;
      e.preventDefault();
      if (isTerminalActive()) {
        showPrevOperatorTrace();
      } else {
        state.pendingTrace = true;
        navigateTo(VIEW_ID);
      }
      return;
    }
  } else {
    konamiPos = key === KONAMI[0] ? 1 : 0;
  }
  // Terminal: a key pressed while the canvas has focus goes to the prompt.
  if (view === VIEW_ID && inputEl && target !== inputEl && e.key.length === 1) {
    e.stopPropagation();
    focusInput();
  }
}

function onWheel(e) {
  if (!isTerminalActive()) return;
  state.scroll = Math.max(0, state.scroll - Math.sign(e.deltaY) * 3);
  e.preventDefault();
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onGlobalKeyDown, { capture: true });
  window.addEventListener('wheel', onWheel, { passive: false });
}

// ----------------------------------------------------------------------- draw

function wrap(text, max) {
  const out = [];
  let rest = String(text);
  if (!rest.length) return [''];
  while (rest.length > max) {
    let cut = rest.lastIndexOf(' ', max);
    if (cut < Math.floor(max / 2)) cut = max;
    out.push(rest.slice(0, cut));
    rest = `  ${rest.slice(cut).trimStart()}`;
  }
  out.push(rest);
  return out;
}

function noise(n) {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

function drawLine(ctx, line, text, x, y, index, time) {
  if (line.k === 'glitch') {
    const tick = Math.floor(time * 6);
    const jitter = noise(index + tick * 7.1) > 0.78 ? (noise(index * 3 + tick) > 0.5 ? 3 : -3) : 0;
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    DopeTextRenderer.renderDOSText(ctx, text, x + jitter + 2, y, 1);
    ctx.fillStyle = noise(index * 1.7 + tick) > 0.3 ? TERMINAL.TEXT : TERMINAL.TEXT_BRIGHT;
    DopeTextRenderer.renderDOSText(ctx, text, x + jitter, y, 1);
    return;
  }
  const color = line.k === 'dim' ? TERMINAL.TEXT_DIM : line.k === 'cmd' || line.k === 'err' ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, line.k === 'err' ? `! ${text}` : text, x, y, 1);
}

registerView(VIEW_ID, function renderTerminalWindow(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ensureInput();
  if (!state.booted) boot();
  if (state.pendingTrace) {
    state.pendingTrace = false;
    showPrevOperatorTrace();
  }

  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const mobile = safe.width < 500;
  const touchH = mobile ? 44 : 24;
  const closeH = mobile ? 44 : 22;

  DopeUI.Button(ctx, safe.width - 72, 1, 64, closeH, 'CLOSE', 'normal');
  interactiveRegistry.register({
    id: 'terminal-close', x: safe.width - 72, y: 1, w: 64, h: closeH, skipInflation: true,
    onClick: () => navigateTo('main_menu'),
  });

  // Bottom row: history up/down, help, submit. 44px on touch.
  const rowY = safe.height - 8 - touchH;
  const gap = 6;
  const btnW = Math.floor((safe.width - 16 - gap * 3) / 4);
  const buttons = [
    { id: 'terminal-hist-up', label: 'UP', run: () => historyStep(-1) },
    { id: 'terminal-hist-down', label: 'DOWN', run: () => historyStep(1) },
    { id: 'terminal-help', label: 'HELP', run: () => submit('help') },
    { id: 'terminal-go', label: 'GO', run: () => { focusInput(); submit(inputEl ? inputEl.value : ''); } },
  ];
  buttons.forEach((b, i) => {
    const bx = 8 + i * (btnW + gap);
    DopeUI.Button(ctx, bx, rowY, btnW, touchH, b.label, 'normal');
    interactiveRegistry.register({ id: b.id, x: bx, y: rowY, w: btnW, h: touchH, skipInflation: true, onClick: b.run });
  });

  // Prompt line (tap to raise the device keyboard).
  const promptH = mobile ? 44 : 22;
  const promptY = rowY - 6 - promptH;
  ctx.fillStyle = TERMINAL.GREEN_DARK;
  ctx.fillRect(8, promptY, safe.width - 16, promptH);
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.strokeRect(8.5, promptY + 0.5, safe.width - 17, promptH - 1);
  interactiveRegistry.register({
    id: 'terminal-prompt', x: 8, y: promptY, w: safe.width - 16, h: promptH, skipInflation: true,
    onClick: focusInput,
  });
  const maxChars = Math.max(12, Math.floor((safe.width - 40) / CHAR_W));
  const value = inputEl ? inputEl.value : '';
  const shown = state.busy ? 'WORKING...' : value;
  const tail = shown.length > maxChars - 3 ? shown.slice(shown.length - (maxChars - 3)) : shown;
  const textY = promptY + Math.floor((promptH - 12) / 2);
  ctx.fillStyle = TERMINAL.TEXT_BRIGHT;
  DopeTextRenderer.renderDOSText(ctx, `> ${tail}`, 14, textY, 1);
  if (!state.busy && Math.floor(time * 2) % 2 === 0) {
    ctx.fillStyle = TERMINAL.TEXT;
    ctx.fillRect(14 + (2 + tail.length) * CHAR_W, textY, 8, 13);
  }

  // Scrollback.
  const panelY = closeH + 4;
  const panelH = promptY - 6 - panelY;
  DopeUI.Panel(ctx, 8, panelY, safe.width - 16, panelH, 'TERMINAL // DOPEOS');
  const wrapW = Math.max(12, Math.floor((safe.width - 40) / CHAR_W));
  const rows = [];
  state.lines.forEach((line, i) => wrap(line.t, wrapW).forEach((t) => rows.push({ line, t, i })));
  const visible = Math.max(1, Math.floor((panelH - 30) / LINE_H));
  const maxScroll = Math.max(0, rows.length - visible);
  state.scroll = Math.min(state.scroll, maxScroll);
  const end = rows.length - state.scroll;
  const slice = rows.slice(Math.max(0, end - visible), end);
  slice.forEach((row, n) => drawLine(ctx, row.line, row.t, 18, panelY + 26 + n * LINE_H, row.i + n, time));
  if (state.scroll > 0) {
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    DopeTextRenderer.renderDOSText(ctx, `-- ${state.scroll} MORE BELOW --`, safe.width - 160, panelY + panelH - 16, 1);
  }
});

export default VIEW_ID;
